// Google Analytics / Tag Manager, loaded only after the visitor accepts cookies.
// Netlify sets GATSBY_GTM_TRACKING_ID (older config used GATSBY_GA_TRACKING_ID); the ID can be
// a GA4 measurement ID (G-…) or a Tag Manager container (GTM-…), and each loads differently.
export const consentStorageKey = "gibraltar-web-studio-cookie-consent"

const trackingId =
  process.env.GATSBY_GTM_TRACKING_ID || process.env.GATSBY_GA_TRACKING_ID
const isTagManager = /^GTM-/i.test(trackingId || "")

const consentState = granted => ({
  analytics_storage: granted ? "granted" : "denied",
  ad_storage: granted ? "granted" : "denied",
  ad_user_data: granted ? "granted" : "denied",
  ad_personalization: granted ? "granted" : "denied",
  functionality_storage: "granted",
  personalization_storage: granted ? "granted" : "denied",
  security_storage: "granted",
})

const ensureGtag = () => {
  window.dataLayer = window.dataLayer || []
  window.gtag =
    window.gtag ||
    function gtag() {
      window.dataLayer.push(arguments)
    }
}

export const hasAnalyticsConsent = () => {
  try {
    return localStorage.getItem(consentStorageKey) === "accepted"
  } catch (e) {
    return false
  }
}

const loadAnalytics = () => {
  if (!trackingId || window.__gwsAnalyticsLoaded) return
  window.__gwsAnalyticsLoaded = true

  const script = document.createElement("script")
  script.async = true
  if (isTagManager) {
    window.dataLayer.push({ "gtm.start": Date.now(), event: "gtm.js" })
    script.src = `https://www.googletagmanager.com/gtm.js?id=${trackingId}`
  } else {
    script.src = `https://www.googletagmanager.com/gtag/js?id=${trackingId}`
    window.gtag("js", new Date())
    window.gtag("config", trackingId, { anonymize_ip: true })
  }
  document.head.appendChild(script)
}

// Send a named event (no-op until analytics has loaded with consent)
export const trackEvent = (name, params = {}) => {
  if (typeof window === "undefined" || !window.__gwsAnalyticsLoaded) return
  if (isTagManager) {
    window.dataLayer.push({ event: name, ...params })
  } else {
    window.gtag("event", name, params)
  }
}

// WhatsApp, email, phone and quote-request links anywhere on the site, including Contentful content
const trackContactClicks = event => {
  const link = event.target.closest && event.target.closest("a[href]")
  if (!link) return
  const href = link.getAttribute("href")
  const linkLocation = link.dataset.analyticsLocation || "page"

  if (/^https?:\/\/(wa\.me|api\.whatsapp\.com)\//i.test(href)) {
    trackEvent("whatsapp_click", { link_location: linkLocation })
  } else if (/^mailto:/i.test(href)) {
    trackEvent("email_click", { link_location: linkLocation })
  } else if (/^tel:/i.test(href)) {
    trackEvent("phone_click", { link_location: linkLocation })
  } else if (/\/website-quote-request\/?$/.test(href)) {
    trackEvent("quote_button_click", {
      link_location: linkLocation,
      link_text: (link.textContent || "").trim().slice(0, 100),
    })
  }
}

// Called once when the site first loads in the browser (gatsby-browser onClientEntry)
export const initAnalytics = () => {
  ensureGtag()
  const granted = hasAnalyticsConsent()
  window.gtag("consent", "default", consentState(granted))
  if (granted) loadAnalytics()
  document.addEventListener("click", trackContactClicks, true)
}

export const acceptAnalytics = () => {
  try {
    localStorage.setItem(consentStorageKey, "accepted")
  } catch (e) {}
  ensureGtag()
  window.gtag("consent", "update", consentState(true))
  loadAnalytics()
}

export const declineAnalytics = () => {
  try {
    localStorage.setItem(consentStorageKey, "declined")
  } catch (e) {}
  ensureGtag()
  window.gtag("consent", "update", consentState(false))
}

// Gatsby changes pages without a full reload. GA4's enhanced measurement records those page
// views itself; Tag Manager containers get an event they can trigger on.
export const trackRouteChange = location => {
  if (isTagManager && window.__gwsAnalyticsLoaded) {
    window.dataLayer.push({
      event: "gatsby_route_change",
      page_path: location.pathname,
    })
  }
}
